import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import {
  useGetDefaultTenantTimeRange,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { isSavedSearch } from '@modules/feed/components/SearchAnalysisToolbar/utils';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { getSearchQueryFromMetadata } from '@modules/feed/utils';
import { FeedbackSummaryActionType } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryActionType';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { ReplayRounded } from '@mui/icons-material';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { isEqual } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';

export const ResetSearchAction = () => {
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();
  const [URLParams, setSearchParams] = useSearchParams();

  const dispatch = useFeedStore(({ dispatch }) => dispatch);
  const isSavedSearchData = useAnalysisStore(({ id }) => isSavedSearch(id));
  const whereClause = useFeedStore(({ query }) => query.whereClause);
  const duration = useFeedStore(({ query }) => query.duration);
  const metadata = useAnalysisStore(({ metadata }) => metadata);
  const feedbackSummaryDispatch = useFeedbackSummaryStore(
    ({ dispatch }) => dispatch,
  );

  const hasFromAndToClause = URLParams.has('from') && URLParams.has('to');

  const defaultTimeRange = useGetDefaultTenantTimeRange();

  const parsedQueryMetadata = metadata
    ? {
        duration,
        ...getSearchQueryFromMetadata({
          metadata,
          getDurationField: !hasFromAndToClause,
          defaultTimeRange,
        }),
      }
    : null;

  const hasNoChangesToReset = (() => {
    if (!isSavedSearchData) {
      return (
        isEqual(whereClause ?? EmptyWhereClause, EmptyWhereClause) &&
        isEqual(duration, defaultTimeRange)
      );
    }

    return (
      !parsedQueryMetadata ||
      (isEqual(whereClause, parsedQueryMetadata.whereClause) &&
        isEqual(duration, parsedQueryMetadata.duration))
    );
  })();

  const onReset = () => {
    if (hasNoChangesToReset) {
      return;
    }

    setSearchParams({});

    if (!isSavedSearchData) {
      dispatch({
        type: SearchActionType.SET_QUERY,
        payload: {
          whereClause: EmptyWhereClause,
          duration: defaultTimeRange,
        },
      });
      feedbackSummaryDispatch({
        type: FeedbackSummaryActionType.RESET,
      });
      return;
    }

    if (parsedQueryMetadata) {
      dispatch({
        type: SearchActionType.SET_QUERY,
        payload: {
          whereClause: parsedQueryMetadata.whereClause,
          duration: parsedQueryMetadata.duration,
        },
      });
      feedbackSummaryDispatch({
        type: FeedbackSummaryActionType.RESET,
      });
    }
  };

  return (
    <Tooltip
      title={
        hasNoChangesToReset
          ? 'No changes to reset'
          : `Reset ${
              isSearchToFeedEnabled ? 'Feed' : 'Search'
            } to original version`
      }
      placement="bottom"
      disableInteractive
    >
      <Stack>
        <IconButton
          color="primary"
          onClick={onReset}
          disabled={hasNoChangesToReset}
          data-role="search-reset-button"
        >
          <ReplayRounded />
        </IconButton>
      </Stack>
    </Tooltip>
  );
};
